// GanttChart.js - Main component with external data support and scroll synchronization
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import TimelineGrid from '../grid/TimelineGrid';
import ActionButtons from './ActionButtons';
import { useGanttLogic } from '../../hooks/useGanttLogic';
import { useExpandedNonWorkingDays } from '../../hooks/useExpandedNonWorkingDays';
import { GanttDndProvider } from '../../context/GanttDndContext';
import { generateSubtitle } from '../../utils/headerUtils';

const GanttChart = ({
  categories = [],
  baseDate = new Date(),
  onTaskClick = null,
  onDateRangeChange = null,
  onTaskMove = null,
  title = null,
  subtitle = null,
  height = '70vh',
  showHeader = true,
  showActions = true,
  showNavigation = true,
  showClosingDays = true,
  enableDnd = true,
  unavailabilityBlocks = [],
}) => {
  const { t } = useTranslation();

  // Expanded non-working days management (public holidays + closing days)
  const {
    nonWorkingDaysMap,
    fetchExpandedNonWorkingDays,
    isNonWorkingDay,
    getNonWorkingDayNames,
    getNonWorkingDayEntries,
  } = useExpandedNonWorkingDays();

  // Handle date range changes - fetch non-working days and notify parent
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    if (showClosingDays) {
      fetchExpandedNonWorkingDays(startDate, endDate);
    }
    if (onDateRangeChange) {
      onDateRangeChange(startDate, endDate);
    }
  }, [showClosingDays, fetchExpandedNonWorkingDays, onDateRangeChange]);

  const {
    scrollContainerRef,
    scrollState,
    categories: processedCategories,
    baseDate: processedBaseDate,
    currentViewDateRange,
    handleScroll,
    scrollToDayIndex,
    jumpToToday,
    jumpToBaseDate,
    DAY_WIDTH,
    ROW_NAME_WIDTH,
    onTaskClick: handleTaskClick,
    viewMode,
    setViewMode,
  } = useGanttLogic({
    categories,
    baseDate,
    onTaskClick,
    onDateRangeChange: handleDateRangeChange,
    // useGanttLogic's ghost-task skip-forward logic keeps the `closingDaysMap` param name
    // (mirrors the backend's Task.skip_closing_days column, which also keeps its name even
    // though it now silently also skips public holidays) — only fed by the renamed map here.
    closingDaysMap: showClosingDays ? nonWorkingDaysMap : null,
  });

  // Fetch non-working days for the initial view range on mount ONLY.
  // Subsequent range growth is already covered by handleDateRangeChange, which
  // useGanttLogic's scroll-expansion effect calls with just the new delta —
  // this effect used to also re-fire on every currentViewDateRange change
  // (which itself changes as a side effect of that same scroll expansion),
  // racing handleDateRangeChange's fetch for the same/overlapping range before
  // either await resolved and updated fetchedRangesRef, so a single scroll
  // event was firing two overlapping network requests. A mount-only guard
  // fixes that without needing to touch the fetch hook's dedup logic.
  const hasFetchedInitialRange = useRef(false);
  useEffect(() => {
    if (showClosingDays && currentViewDateRange && !hasFetchedInitialRange.current) {
      hasFetchedInitialRange.current = true;
      fetchExpandedNonWorkingDays(currentViewDateRange.startDate, currentViewDateRange.endDate);
    }
  }, [showClosingDays, currentViewDateRange, fetchExpandedNonWorkingDays]);


  // Get current date being viewed
  const currentViewDate = new Date(processedBaseDate);
  currentViewDate.setDate(currentViewDate.getDate() + scrollToDayIndex(scrollState.scrollLeft));

  // Generate subtitle if not provided
  const displaySubtitle = subtitle || generateSubtitle(processedCategories, currentViewDate, t);

  // Use default title from translations if not provided
  const displayTitle = title || t('common:gantt.title');

  return (
    <GanttDndProvider onTaskMove={onTaskMove}>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        {showHeader && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {displayTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
              {displaySubtitle}
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mt: 0.5 }}>
              {t('common:gantt.base_date')}: {processedBaseDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </Typography>
          </Box>
        )}

        {/* Main Chart Container */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Paper
            elevation={3}
            sx={{
              flex: 1,
              borderRadius: 3,
              overflow: 'hidden',
              display: 'flex',
              minHeight: 0,
            }}
          >
            {processedCategories.length > 0 || true ? (
              <TimelineGrid
                ref={scrollContainerRef}
                onScroll={handleScroll}
                scrollState={scrollState}
                categories={processedCategories}
                baseDate={processedBaseDate}
                dayWidth={DAY_WIDTH}
                rowNameWidth={ROW_NAME_WIDTH}
                onTaskClick={handleTaskClick}
                nonWorkingDaysMap={showClosingDays ? nonWorkingDaysMap : null}
                isNonWorkingDay={showClosingDays ? isNonWorkingDay : null}
                getNonWorkingDayNames={showClosingDays ? getNonWorkingDayNames : null}
                getNonWorkingDayEntries={showClosingDays ? getNonWorkingDayEntries : null}
                unavailabilityBlocks={unavailabilityBlocks}
              />
            ) : (
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('common:gantt.no_data')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('common:gantt.no_data_subtitle')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Action Buttons */}
        {showActions && processedCategories.length > 0 && (
          <ActionButtons
            onJumpToToday={jumpToToday}
            onJumpToBaseDate={jumpToBaseDate}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            enableDnd={enableDnd}
          />
        )}
      </Box>
    </GanttDndProvider>
  );
};

export default GanttChart;
